/**
 * Merchant Portal - /m/[id]
 * 
 * Full user experience mirror (same as regular user dashboard)
 * EXCLUDES: All pricing, billing, plans information
 * 
 * Features:
 * - Create+ (content creation - same as regular user)
 * - Calendar view
 * - Posts management
 * - Analytics
 * - Settings with multi-profile support
 * - Inspiration Hub
 * - All templates and languages
 * - Auto-Pilot (uses allocated credits, no auto-billing)
 */

import { useState, useEffect, createContext, useContext } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store,
  Image,
  Video,
  Plus,
  BarChart3,
  Calendar,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle,
  FileText,
  Sparkles,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Settings,
  Lightbulb,
  LayoutDashboard,
  Bell,
  Zap,
  Target,
  Gift,
  HelpCircle,
  BookOpen,
  User,
  Check,
  PlusCircle,
  MessageSquare,
  TrendingUp,
  Eye,
  Heart,
  Share2,
  Hash,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DocumentsSection from "@/components/DocumentsSection";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

// Merchant Context for sharing data across components
const MerchantContext = createContext(null);

export const useMerchant = () => {
  const context = useContext(MerchantContext);
  if (!context) {
    throw new Error("useMerchant must be used within MerchantProvider");
  }
  return context;
};

// Merchant navigation items - same as regular user but without billing
const merchantNavItems = [
  { icon: HelpCircle, label: "How To Use", path: "#guide", isGuide: true },
  { icon: LayoutDashboard, label: "Dashboard", path: "dashboard" },
  { icon: Plus, label: "Create", path: "create" },
  { icon: Lightbulb, label: "Content Ideas", path: "ideas" },
  { icon: FileText, label: "Posts", path: "posts" },
  { icon: Calendar, label: "Calendar", path: "calendar" },
  { icon: Sparkles, label: "Viral Simulator", path: "viral-simulator" },
  { icon: BarChart3, label: "Analytics", path: "analytics" },
  { icon: BookOpen, label: "Documents", path: "documents" },
];

// Quick Guide content for merchants
const guideSteps = [
  {
    title: "1. Choose Your Input",
    icon: Plus,
    description: "Upload images, paste a URL, or speak/type your idea. Multiple images supported for collages!",
    tips: ["Drag & drop multiple images for collages", "Voice input for hands-free creation", "Works with blogs, products, and articles"]
  },
  {
    title: "2. Customize & Design",
    icon: FileText,
    description: "Choose AI Generated or FREE Templates. Add listing details and drag fields to position them on your post.",
    tips: ["Drag & drop field positioning", "Custom text styling (font, color, size)", "Works for both AI and Templates"]
  },
  {
    title: "3. Add Your Personal Touch",
    icon: User,
    description: "Feature Yourself (add headshot) OR Personal Branding (banner with logo). Choose one for professional posts.",
    tips: ["Feature Yourself = headshot overlay", "Personal Branding = full banner", "Cannot use both together"]
  },
  {
    title: "4. Select Platforms",
    icon: Target,
    description: "Choose Facebook, Instagram, TikTok, Pinterest. Each gets optimized captions and hashtags.",
    tips: ["Multi-platform posting", "Platform-specific sizing", "Viral score predictions"]
  },
  {
    title: "5. Generate & Schedule",
    icon: Calendar,
    description: "Generate your content, then download or schedule directly to connected social accounts.",
    tips: ["Calendar view for planning", "Bulk content creation", "Connect accounts in Settings"]
  }
];

// Video credit cost reference
const VIDEO_CREDIT_COSTS = {
  "10s 720p": 1,
  "20s 720p": 2,
  "10s 1080p": 2,
  "20s 1080p": 4,
  "30s 720p": 3,
  "30s 1080p": 6
};

export default function MerchantPortal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [merchant, setMerchant] = useState(null);
  const [enterprise, setEnterprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Login
  const [accessCode, setAccessCode] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // UI State
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Content creation state
  const [templates, setTemplates] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  
  useEffect(() => {
    // Check if already authenticated (coming from /merchant login page)
    const token = localStorage.getItem(`merchantToken_${id}`);
    const accessVerified = localStorage.getItem("merchantAccessVerified");
    
    if (token) {
      // Clear the verified flag as it's now used
      if (accessVerified) {
        localStorage.removeItem("merchantAccessVerified");
      }
      fetchMerchantData(id, token);
    } else {
      setLoading(false);
    }
  }, [id]);
  
  const fetchMerchantData = async (merchantId, token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await apiClient.get(`/b2b/merchants/${merchantId}`, { headers });
      setMerchant(res.data);
      setEnterprise(res.data.enterprise);
      setIsLoggedIn(true);
      
      // Fetch additional data for merchant
      await Promise.all([
        fetchTemplates(),
        fetchLanguages(),
        fetchRecentPosts(merchantId, token),
        fetchNotifications(merchantId, token)
      ]);
    } catch (error) {
      console.error("Error fetching merchant data:", error);
      localStorage.removeItem(`merchantToken_${id}`);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const res = await apiClient.get("/templates?limit=50");
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };
  
  const fetchLanguages = async () => {
    try {
      const res = await apiClient.get("/languages");
      setLanguages(res.data.languages || []);
    } catch (err) {
      console.error("Failed to fetch languages:", err);
    }
  };
  
  const fetchRecentPosts = async (merchantId, token) => {
    try {
      // For now, use mock data - in production, this would fetch merchant's posts
      setRecentPosts([]);
    } catch (err) {
      console.error("Failed to fetch posts:", err);
    }
  };
  
  const fetchNotifications = async (merchantId, token) => {
    // Merchant notifications - exclude billing/payment related
    setNotifications([
      { id: 1, text: "Welcome to Viraly! Create your first post.", type: "info", time: "Now" }
    ]);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!accessCode) {
      toast.error("Please enter your access code");
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await apiClient.post("/b2b/merchants/login", {
        merchant_id: id,
        access_code: accessCode.toUpperCase()
      });
      
      if (res.data.merchant_id) {
        localStorage.setItem(`merchantToken_${id}`, res.data.token || "merchant-session");
        await fetchMerchantData(id, res.data.token);
        toast.success("Welcome!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code");
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email address");
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      await apiClient.post("/b2b/forgot-password", {
        email: forgotPasswordEmail,
        portal_type: "merchant"
      });
      toast.success("If your email exists in our system, you will receive a reset link");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (error) {
      toast.error("Failed to send reset link");
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem(`merchantToken_${id}`);
    setIsLoggedIn(false);
    setMerchant(null);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-viraly-orange animate-spin" />
      </div>
    );
  }
  
  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-viraly-dark flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-green-600/20 flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-viraly-light">Merchant Portal</h1>
            <p className="text-viraly-muted mt-2">Create AI-powered content for your business</p>
          </div>
          
          <form onSubmit={handleLogin} className="card-base p-6 space-y-4">
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Access Code</label>
              <Input
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="M-XXXXXXXX"
                className="bg-white/5 border-white/10 uppercase text-center text-lg tracking-widest"
                data-testid="merchant-access-code"
              />
              <p className="text-xs text-viraly-muted mt-2 text-center">
                Your access code was provided by your service provider
              </p>
            </div>
            
            <Button type="submit" disabled={loginLoading} className="w-full btn-primary" data-testid="merchant-login-btn">
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Access Portal
            </Button>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="w-full text-sm text-viraly-muted hover:text-viraly-orange transition-colors"
            >
              Forgot your access code?
            </button>
          </form>
        </motion.div>
        
        {/* Forgot Password Modal */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="bg-viraly-card border-white/10">
            <DialogHeader>
              <DialogTitle>Reset Access Code</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-viraly-muted">
                Enter your email address and we'll send you a link to reset your access code.
              </p>
              <Input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white/5 border-white/10"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowForgotPassword(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleForgotPassword} disabled={forgotPasswordLoading} className="flex-1 btn-primary">
                  {forgotPasswordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send Reset Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Calculate credit usage
  const postCreditsAvailable = merchant?.post_credits_available || 0;
  const videoCreditsAvailable = merchant?.video_credits_available || 0;
  const postsUsed = merchant?.posts_used_this_month || 0;
  const videosUsed = merchant?.videos_used_this_month || 0;
  
  return (
    <MerchantContext.Provider value={{ merchant, enterprise, setMerchant }}>
      <div className="min-h-screen bg-viraly-dark flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 bg-viraly-card border-r border-white/5 fixed h-full">
          <div className="p-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
              <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
            </div>
            <p className="text-xs text-viraly-muted mt-1">Powered by {enterprise?.company_name || "Your Provider"}</p>
          </div>
          
          <nav className="flex-1 px-4">
            <ul className="space-y-1">
              {merchantNavItems.map((item) => {
                const isActive = activeTab === item.path;
                
                if (item.isGuide) {
                  return (
                    <li key={item.path}>
                      <button
                        onClick={() => setShowGuide(true)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-viraly-orange hover:text-white hover:bg-viraly-orange/10 border border-viraly-orange/30"
                        data-testid="merchant-nav-guide"
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                        <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold bg-viraly-orange/20 text-viraly-orange">
                          GUIDE
                        </span>
                      </button>
                    </li>
                  );
                }
                
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => setActiveTab(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive 
                          ? "bg-viraly-gradient text-white" 
                          : "text-viraly-muted hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`merchant-nav-${item.path}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          <div className="p-4 border-t border-white/5">
            <button
              onClick={() => setActiveTab("settings")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === "settings"
                  ? "bg-viraly-gradient text-white"
                  : "text-viraly-muted hover:text-white hover:bg-white/5"
              }`}
              data-testid="merchant-nav-settings"
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 z-50"
            >
              <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setSidebarOpen(false)}
              />
              <motion.aside
                initial={{ x: -256 }}
                animate={{ x: 0 }}
                exit={{ x: -256 }}
                className="absolute left-0 top-0 h-full w-64 bg-viraly-card border-r border-white/5"
              >
                <div className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
                    <span className="font-heading font-bold text-2xl gradient-text">Viraly</span>
                  </div>
                  <button onClick={() => setSidebarOpen(false)}>
                    <X className="w-6 h-6 text-viraly-muted" />
                  </button>
                </div>
                
                <nav className="px-4">
                  <ul className="space-y-1">
                    {merchantNavItems.map((item) => {
                      const isActive = activeTab === item.path;
                      
                      if (item.isGuide) {
                        return (
                          <li key={item.path}>
                            <button
                              onClick={() => {
                                setSidebarOpen(false);
                                setShowGuide(true);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-viraly-orange hover:text-white hover:bg-viraly-orange/10 border border-viraly-orange/30"
                            >
                              <item.icon className="w-5 h-5" />
                              <span className="font-medium">{item.label}</span>
                            </button>
                          </li>
                        );
                      }
                      
                      return (
                        <li key={item.path}>
                          <button
                            onClick={() => {
                              setActiveTab(item.path);
                              setSidebarOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                              isActive 
                                ? "bg-viraly-gradient text-white" 
                                : "text-viraly-muted hover:text-white hover:bg-white/5"
                            }`}
                          >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                    <li>
                      <button
                        onClick={() => {
                          setActiveTab("settings");
                          setSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          activeTab === "settings"
                            ? "bg-viraly-gradient text-white"
                            : "text-viraly-muted hover:text-white hover:bg-white/5"
                        }`}
                      >
                        <Settings className="w-5 h-5" />
                        <span>Settings</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </motion.aside>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="flex-1 lg:ml-64">
          {/* Top Bar */}
          <header className="sticky top-0 z-40 glass-card border-0 border-b border-white/5 px-6 py-4">
            <div className="flex items-center justify-between">
              <button 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="merchant-mobile-menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <div className="hidden lg:flex items-center gap-2">
                <Store className="w-5 h-5 text-green-400" />
                <span className="font-bold text-viraly-light">{merchant?.business_name}</span>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Credits Display */}
                <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-blue-400" />
                    <span className="text-sm">
                      <span className="text-viraly-light font-medium">{postCreditsAvailable}</span>
                      <span className="text-viraly-muted"> post credits</span>
                    </span>
                  </div>
                  <div className="w-px h-4 bg-white/10" />
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-purple-400" />
                    <span className="text-sm">
                      <span className="text-viraly-light font-medium">{videoCreditsAvailable}</span>
                      <span className="text-viraly-muted"> video credits</span>
                    </span>
                  </div>
                </div>
                
                {/* Notifications */}
                <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="relative p-2 rounded-xl hover:bg-white/10 transition-colors"
                      data-testid="merchant-notifications"
                    >
                      <Bell className="w-5 h-5 text-white" />
                      {notifications.length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-viraly-orange rounded-full text-[10px] font-bold flex items-center justify-center">
                          {notifications.length}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-viraly-card border-white/10">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="font-medium">Notifications</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.map((notif) => (
                        <div key={notif.id} className="px-4 py-3 hover:bg-white/5 border-b border-white/5">
                          <p className="text-sm">{notif.text}</p>
                          <p className="text-xs text-viraly-muted mt-1">{notif.time}</p>
                        </div>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="merchant-user-menu">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-green-400">
                          {merchant?.business_name?.charAt(0).toUpperCase() || "M"}
                        </span>
                      </div>
                      <span className="hidden md:block text-sm text-viraly-light">{merchant?.contact_name}</span>
                      <ChevronDown className="w-4 h-4 text-viraly-muted" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-viraly-card border-white/10">
                    <div className="px-3 py-2">
                      <p className="font-medium">{merchant?.business_name}</p>
                      <p className="text-sm text-viraly-muted">{merchant?.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={() => setActiveTab("settings")} className="cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-viraly-error">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-6 overflow-y-auto max-h-[calc(100vh-80px)]">
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && (
              <MerchantDashboard 
                merchant={merchant} 
                enterprise={enterprise}
                postCreditsAvailable={postCreditsAvailable}
                videoCreditsAvailable={videoCreditsAvailable}
                postsUsed={postsUsed}
                videosUsed={videosUsed}
                onNavigate={setActiveTab}
              />
            )}
            
            {/* Create Tab */}
            {activeTab === "create" && (
              <MerchantCreate 
                merchant={merchant}
                templates={templates}
                languages={languages}
                postCreditsAvailable={postCreditsAvailable}
                videoCreditsAvailable={videoCreditsAvailable}
              />
            )}
            
            {/* Content Ideas Tab */}
            {activeTab === "ideas" && (
              <MerchantContentIdeas merchant={merchant} />
            )}
            
            {/* Posts Tab */}
            {activeTab === "posts" && (
              <MerchantPosts merchant={merchant} recentPosts={recentPosts} />
            )}
            
            {/* Calendar Tab */}
            {activeTab === "calendar" && (
              <MerchantCalendar merchant={merchant} scheduledPosts={scheduledPosts} />
            )}
            
            {/* Viral Simulator Tab */}
            {activeTab === "viral-simulator" && (
              <MerchantViralSimulator merchant={merchant} />
            )}
            
            {/* Analytics Tab */}
            {activeTab === "analytics" && (
              <MerchantAnalytics merchant={merchant} />
            )}
            
            {/* Documents Tab */}
            {activeTab === "documents" && (
              <DocumentsSection
                entityType="merchant"
                entityId={merchant?.id}
                entityName={merchant?.business_name || merchant?.contact_name}
                entityEmail={merchant?.email}
                entityTitle="Merchant Representative"
                accentColor="blue"
              />
            )}
            
            {/* Settings Tab */}
            {activeTab === "settings" && (
              <MerchantSettings merchant={merchant} setMerchant={setMerchant} />
            )}
          </main>
        </div>
        
        {/* Quick Guide Modal */}
        <Dialog open={showGuide} onOpenChange={setShowGuide}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-viraly-card border-viraly-border">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading flex items-center gap-3">
                <BookOpen className="w-7 h-7 text-viraly-orange" />
                <span className="gradient-text">How To Use</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {guideSteps.map((step, index) => (
                <div 
                  key={index}
                  className="bg-viraly-dark/50 rounded-xl p-5 border border-white/5 hover:border-viraly-orange/30 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-viraly-orange/10 text-viraly-orange">
                      <step.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                      <p className="text-viraly-muted mb-3">{step.description}</p>
                      <div className="space-y-1">
                        {step.tips.map((tip, tipIdx) => (
                          <div key={tipIdx} className="flex items-center gap-2 text-sm text-viraly-muted/80">
                            <Zap className="w-3 h-3 text-viraly-orange" />
                            <span>{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Video Credits Info */}
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-400" />
                  Video Credit Costs
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(VIDEO_CREDIT_COSTS).map(([quality, cost]) => (
                    <div key={quality} className="bg-viraly-dark/50 rounded-lg p-3">
                      <p className="text-sm text-viraly-light">{quality}</p>
                      <p className="text-xs text-purple-400">{cost} video credit{cost > 1 ? "s" : ""}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Quick Links */}
              <div className="bg-viraly-gradient/10 rounded-xl p-5 border border-viraly-orange/20">
                <h3 className="text-lg font-semibold text-white mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button onClick={() => { setShowGuide(false); setActiveTab("create"); }} className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                    <Plus className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-white">Create Post</span>
                  </button>
                  <button onClick={() => { setShowGuide(false); setActiveTab("posts"); }} className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                    <FileText className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-white">My Posts</span>
                  </button>
                  <button onClick={() => { setShowGuide(false); setActiveTab("calendar"); }} className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                    <Calendar className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-white">Calendar</span>
                  </button>
                  <button onClick={() => { setShowGuide(false); setActiveTab("ideas"); }} className="flex items-center gap-2 px-3 py-2 bg-viraly-dark rounded-lg hover:bg-white/5 transition-all">
                    <Lightbulb className="w-4 h-4 text-viraly-orange" />
                    <span className="text-sm text-white">Content Ideas</span>
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MerchantContext.Provider>
  );
}

// ==================== SUB-COMPONENTS ====================

function MerchantDashboard({ merchant, enterprise, postCreditsAvailable, videoCreditsAvailable, postsUsed, videosUsed, onNavigate }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-viraly-light">Welcome back, {merchant?.contact_name || "there"}!</h1>
          <p className="text-viraly-muted">Here's your content creation overview</p>
        </div>
        <Button onClick={() => onNavigate("create")} className="btn-primary" data-testid="merchant-create-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Content
        </Button>
      </div>
      
      {/* Credit Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Image className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Post Credits</p>
              <p className="text-2xl font-bold text-viraly-light">{postCreditsAvailable}</p>
            </div>
          </div>
          <p className="text-xs text-viraly-muted">Available for content creation</p>
        </div>
        
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Video className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Video Credits</p>
              <p className="text-2xl font-bold text-viraly-light">{videoCreditsAvailable}</p>
            </div>
          </div>
          <p className="text-xs text-viraly-muted">For video generation</p>
        </div>
        
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Posts Created</p>
              <p className="text-2xl font-bold text-viraly-light">{postsUsed}</p>
            </div>
          </div>
          <p className="text-xs text-viraly-muted">This month</p>
        </div>
        
        <div className="card-base p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-viraly-muted">Videos Created</p>
              <p className="text-2xl font-bold text-viraly-light">{videosUsed}</p>
            </div>
          </div>
          <p className="text-xs text-viraly-muted">This month</p>
        </div>
      </div>
      
      {/* Video Credits Info */}
      <div className="card-base p-6">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-purple-400" />
          <h3 className="font-bold text-viraly-light">Video Credit Costs</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(VIDEO_CREDIT_COSTS).map(([quality, cost]) => (
            <div key={quality} className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-sm text-viraly-light font-medium">{quality}</p>
              <p className="text-xs text-purple-400 mt-1">{cost} credit{cost > 1 ? "s" : ""}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => onNavigate("create")}
          className="card-base p-6 hover:border-viraly-orange/30 transition-all text-left group"
        >
          <Plus className="w-8 h-8 text-viraly-orange mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-viraly-light mb-1">Create Post</h3>
          <p className="text-xs text-viraly-muted">Generate AI-powered content</p>
        </button>
        
        <button
          onClick={() => onNavigate("ideas")}
          className="card-base p-6 hover:border-yellow-500/30 transition-all text-left group"
        >
          <Lightbulb className="w-8 h-8 text-yellow-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-viraly-light mb-1">Content Ideas</h3>
          <p className="text-xs text-viraly-muted">Get inspired with trending topics</p>
        </button>
        
        <button
          onClick={() => onNavigate("calendar")}
          className="card-base p-6 hover:border-blue-500/30 transition-all text-left group"
        >
          <Calendar className="w-8 h-8 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-viraly-light mb-1">Schedule</h3>
          <p className="text-xs text-viraly-muted">Plan your content calendar</p>
        </button>
        
        <button
          onClick={() => onNavigate("analytics")}
          className="card-base p-6 hover:border-green-500/30 transition-all text-left group"
        >
          <BarChart3 className="w-8 h-8 text-green-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-bold text-viraly-light mb-1">Analytics</h3>
          <p className="text-xs text-viraly-muted">Track your performance</p>
        </button>
      </div>
      
      {/* Provider Info */}
      <div className="card-base p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <p className="text-sm text-viraly-muted">Powered by</p>
            <h3 className="font-bold text-viraly-light">{enterprise?.company_name || "Your Provider"}</h3>
          </div>
        </div>
        <p className="text-xs text-viraly-muted mt-4">
          Your content credits are managed by your service provider. Contact them for additional credits or support.
        </p>
      </div>
    </div>
  );
}

function MerchantCreate({ merchant, templates, languages, postCreditsAvailable, videoCreditsAvailable }) {
  const [contentType, setContentType] = useState("post");
  const [contentInput, setContentInput] = useState("");
  const [generating, setGenerating] = useState(false);
  
  const createContent = async () => {
    if (!contentInput.trim()) {
      toast.error("Please enter content to create");
      return;
    }
    
    if (contentType === "post" && postCreditsAvailable <= 0) {
      toast.error("No post credits available. Contact your provider.");
      return;
    }
    if (contentType === "video" && videoCreditsAvailable <= 0) {
      toast.error("No video credits available. Contact your provider.");
      return;
    }
    
    setGenerating(true);
    try {
      // This would call the actual content generation API
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulated
      toast.success(`${contentType === 'post' ? 'Post' : 'Video'} created successfully!`);
      setContentInput("");
    } catch (error) {
      toast.error("Failed to create content");
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Create Content</h1>
        <p className="text-viraly-muted">Generate AI-powered posts and videos for your business</p>
      </div>
      
      {/* Credits Available */}
      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-viraly-light">{postCreditsAvailable} post credits available</span>
        </div>
        <div className="w-px h-4 bg-white/10" />
        <div className="flex items-center gap-2">
          <Video className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-viraly-light">{videoCreditsAvailable} video credits available</span>
        </div>
      </div>
      
      {/* Content Type Selector */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setContentType("post")}
          disabled={postCreditsAvailable <= 0}
          className={`p-6 rounded-xl border-2 transition-all ${
            contentType === "post"
              ? "border-blue-500 bg-blue-500/10"
              : "border-white/10 hover:border-white/20"
          } ${postCreditsAvailable <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Image className={`w-8 h-8 mx-auto mb-3 ${contentType === "post" ? "text-blue-400" : "text-viraly-muted"}`} />
          <p className={`font-medium ${contentType === "post" ? "text-blue-400" : "text-viraly-light"}`}>
            Image Post
          </p>
          <p className="text-xs text-viraly-muted mt-1">1 post credit</p>
        </button>
        
        <button
          onClick={() => setContentType("video")}
          disabled={videoCreditsAvailable <= 0}
          className={`p-6 rounded-xl border-2 transition-all ${
            contentType === "video"
              ? "border-purple-500 bg-purple-500/10"
              : "border-white/10 hover:border-white/20"
          } ${videoCreditsAvailable <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Video className={`w-8 h-8 mx-auto mb-3 ${contentType === "video" ? "text-purple-400" : "text-viraly-muted"}`} />
          <p className={`font-medium ${contentType === "video" ? "text-purple-400" : "text-viraly-light"}`}>
            Video
          </p>
          <p className="text-xs text-viraly-muted mt-1">1-6 video credits</p>
        </button>
      </div>
      
      {/* Content Input */}
      <div className="card-base p-6">
        <label className="text-sm text-viraly-muted block mb-2">
          Describe your {contentType === 'post' ? 'image' : 'video'} content
        </label>
        <Textarea
          value={contentInput}
          onChange={(e) => setContentInput(e.target.value)}
          placeholder={`What ${contentType} would you like to create? Be descriptive for best results...`}
          className="bg-white/5 border-white/10 min-h-[150px] mb-4"
        />
        
        <Button
          onClick={createContent}
          disabled={generating || !contentInput.trim()}
          className="w-full btn-primary"
          data-testid="merchant-generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Create {contentType === 'post' ? 'Post' : 'Video'}
            </>
          )}
        </Button>
      </div>
      
      {/* Templates Section */}
      <div className="card-base p-6">
        <h3 className="font-bold text-viraly-light mb-4">Available Templates ({templates.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {templates.slice(0, 12).map((template, idx) => (
            <div key={idx} className="bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-all cursor-pointer">
              <div className="aspect-square bg-white/10 rounded mb-2 flex items-center justify-center">
                <FileText className="w-6 h-6 text-viraly-muted" />
              </div>
              <p className="text-xs text-viraly-light truncate">{template.name || `Template ${idx + 1}`}</p>
            </div>
          ))}
          {templates.length > 12 && (
            <div className="bg-white/5 rounded-lg p-3 flex items-center justify-center">
              <p className="text-xs text-viraly-muted">+{templates.length - 12} more</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MerchantContentIdeas({ merchant }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Content Ideas</h1>
        <p className="text-viraly-muted">Get inspired with trending topics and content suggestions</p>
      </div>
      
      <div className="card-base p-8 text-center">
        <Lightbulb className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
        <h3 className="font-bold text-viraly-light mb-2">Content Ideas Hub</h3>
        <p className="text-viraly-muted mb-4">
          Discover trending topics, content suggestions, and inspiration for your posts.
        </p>
        <Button className="btn-primary">
          <Sparkles className="w-4 h-4 mr-2" />
          Generate Ideas
        </Button>
      </div>
    </div>
  );
}

function MerchantPosts({ merchant, recentPosts }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-viraly-light">My Posts</h1>
          <p className="text-viraly-muted">View and manage your created content</p>
        </div>
      </div>
      
      {recentPosts.length === 0 ? (
        <div className="card-base p-8 text-center">
          <FileText className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
          <h3 className="font-bold text-viraly-light mb-2">No Posts Yet</h3>
          <p className="text-viraly-muted">Create your first post to see it here.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {recentPosts.map((post, idx) => (
            <div key={idx} className="card-base p-4 flex items-center gap-4">
              <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center">
                <Image className="w-6 h-6 text-viraly-muted" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-viraly-light">{post.title || "Untitled Post"}</p>
                <p className="text-xs text-viraly-muted">{post.created_at || "Recently"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MerchantCalendar({ merchant, scheduledPosts }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Content Calendar</h1>
        <p className="text-viraly-muted">Schedule and plan your content</p>
      </div>
      
      <div className="card-base p-8 text-center">
        <Calendar className="w-12 h-12 text-blue-400 mx-auto mb-4" />
        <h3 className="font-bold text-viraly-light mb-2">Calendar View</h3>
        <p className="text-viraly-muted mb-4">
          Plan your content schedule and see upcoming posts.
        </p>
        {scheduledPosts.length === 0 && (
          <p className="text-xs text-viraly-muted">No scheduled posts yet.</p>
        )}
      </div>
    </div>
  );
}

function MerchantViralSimulator({ merchant }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Viral Simulator</h1>
        <p className="text-viraly-muted">Predict the viral potential of your content</p>
      </div>
      
      <div className="card-base p-8 text-center">
        <Sparkles className="w-12 h-12 text-viraly-orange mx-auto mb-4" />
        <h3 className="font-bold text-viraly-light mb-2">Test Your Content</h3>
        <p className="text-viraly-muted mb-4">
          Analyze your content to predict its viral potential before posting.
        </p>
        <Button className="btn-primary">
          <TrendingUp className="w-4 h-4 mr-2" />
          Start Simulation
        </Button>
      </div>
    </div>
  );
}

function MerchantAnalytics({ merchant }) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Analytics</h1>
        <p className="text-viraly-muted">Track your content performance</p>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-base p-6 text-center">
          <Eye className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-viraly-light">0</p>
          <p className="text-xs text-viraly-muted">Total Views</p>
        </div>
        <div className="card-base p-6 text-center">
          <Heart className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-viraly-light">0</p>
          <p className="text-xs text-viraly-muted">Total Likes</p>
        </div>
        <div className="card-base p-6 text-center">
          <MessageSquare className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-viraly-light">0</p>
          <p className="text-xs text-viraly-muted">Total Comments</p>
        </div>
        <div className="card-base p-6 text-center">
          <Share2 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-viraly-light">0</p>
          <p className="text-xs text-viraly-muted">Total Shares</p>
        </div>
      </div>
      
      <div className="card-base p-8 text-center">
        <BarChart3 className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
        <h3 className="font-bold text-viraly-light mb-2">Analytics Dashboard</h3>
        <p className="text-viraly-muted">
          Connect your social accounts to see detailed analytics.
        </p>
      </div>
    </div>
  );
}

function MerchantSettings({ merchant, setMerchant }) {
  const [activeSettingsTab, setActiveSettingsTab] = useState("profile");
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-viraly-light">Settings</h1>
        <p className="text-viraly-muted">Manage your account and preferences</p>
      </div>
      
      {/* Settings Tabs - NO BILLING TAB FOR MERCHANTS */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {[
          { id: "profile", label: "Profile", icon: User },
          { id: "social", label: "Social Accounts", icon: Share2 },
          { id: "notifications", label: "Notifications", icon: Bell },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSettingsTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeSettingsTab === tab.id
                ? "bg-viraly-gradient text-white"
                : "text-viraly-muted hover:text-viraly-light hover:bg-white/5"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Profile Settings */}
      {activeSettingsTab === "profile" && (
        <div className="card-base p-6 space-y-4">
          <h3 className="font-bold text-viraly-light">Business Profile</h3>
          <div className="grid gap-4">
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Business Name</label>
              <Input value={merchant?.business_name || ""} disabled className="bg-white/5 border-white/10" />
            </div>
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Contact Name</label>
              <Input value={merchant?.contact_name || ""} disabled className="bg-white/5 border-white/10" />
            </div>
            <div>
              <label className="text-sm text-viraly-muted block mb-1">Email</label>
              <Input value={merchant?.email || ""} disabled className="bg-white/5 border-white/10" />
            </div>
          </div>
          <p className="text-xs text-viraly-muted">
            Contact your service provider to update your business information.
          </p>
        </div>
      )}
      
      {/* Social Accounts */}
      {activeSettingsTab === "social" && (
        <div className="card-base p-6">
          <h3 className="font-bold text-viraly-light mb-4">Connected Accounts</h3>
          <p className="text-viraly-muted text-sm mb-4">
            Connect your social media accounts to schedule and post content directly.
          </p>
          <div className="space-y-3">
            {["Facebook", "Instagram", "TikTok", "Pinterest"].map((platform) => (
              <div key={platform} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <span className="text-viraly-light">{platform}</span>
                <Button variant="outline" size="sm">Connect</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Notifications */}
      {activeSettingsTab === "notifications" && (
        <div className="card-base p-6">
          <h3 className="font-bold text-viraly-light mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            {[
              { id: "post_created", label: "Post creation alerts", description: "Get notified when your content is ready" },
              { id: "schedule_reminder", label: "Schedule reminders", description: "Reminders for upcoming scheduled posts" },
              { id: "tips", label: "Tips & suggestions", description: "Content tips and best practices" },
            ].map((pref) => (
              <div key={pref.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <p className="text-sm text-viraly-light">{pref.label}</p>
                  <p className="text-xs text-viraly-muted">{pref.description}</p>
                </div>
                <input type="checkbox" defaultChecked className="w-4 h-4" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
